/**
 * PCM 音频播放器
 * 用于播放流式 PCM 音频数据
 */
export class PCMAudioPlayer {
  private sampleRate: number;
  private audioContext: AudioContext | null = null;
  private audioQueue: ArrayBuffer[] = [];
  private isPlaying = false;
  private currentSource: AudioBufferSourceNode | null = null;

  constructor(sampleRate: number) {
    this.sampleRate = sampleRate;
  }

  /**
   * 初始化音频上下文
   */
  connect(): void {
    if (!this.audioContext) {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioContext = new AudioContextClass();
    }
  }

  /**
   * 添加 PCM 音频数据到队列并播放
   * @param arrayBuffer PCM 音频数据
   */
  pushPCM(arrayBuffer: ArrayBuffer): void {
    this.audioQueue.push(arrayBuffer);
    this.playNextAudio();
  }

  /**
   * 将 ArrayBuffer 转换为 AudioBuffer
   * @param pcmData PCM 音频数据
   * @returns AudioBuffer
   */
  private bufferPCMData(pcmData: ArrayBuffer): AudioBuffer {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized. Call connect() first.');
    }

    const sampleRate = this.sampleRate;
    // 假设 PCM 数据为 16 位，每个样本 2 字节
    const length = pcmData.byteLength / 2;
    const audioBuffer = this.audioContext.createBuffer(1, length, sampleRate);
    const channelData = audioBuffer.getChannelData(0);
    const int16Array = new Int16Array(pcmData);

    // 将 16 位 PCM 转换为浮点数 (-1.0 到 1.0)
    for (let i = 0; i < length; i++) {
      channelData[i] = int16Array[i] / 32768;
    }

    // const audioLength = (length / sampleRate) * 1000;
    // if (import.meta.env.DEV) {
    //   // eslint-disable-next-line no-console
    //   console.log(`prepare audio: ${length} samples, ${audioLength} ms`);
    // }

    return audioBuffer;
  }

  /**
   * 播放音频
   * @param arrayBuffer PCM 音频数据
   */
  private async playAudio(arrayBuffer: ArrayBuffer): Promise<void> {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized. Call connect() first.');
    }

    // 如果音频上下文被暂停，恢复它
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    const audioBuffer = this.bufferPCMData(arrayBuffer);

    this.currentSource = this.audioContext.createBufferSource();
    this.currentSource.buffer = audioBuffer;
    this.currentSource.connect(this.audioContext.destination);

    this.currentSource.onended = () => {
      // if (import.meta.env.DEV) {
      //   // eslint-disable-next-line no-console
      //   console.log('Audio playback ended.');
      // }
      this.isPlaying = false;
      this.currentSource = null;
      this.playNextAudio(); // 播放队列中的下一个音频
    };

    this.currentSource.start();
    this.isPlaying = true;
  }

  /**
   * 播放队列中的下一个音频
   */
  private playNextAudio(): void {
    if (this.audioQueue.length > 0 && !this.isPlaying) {
      // 计算总的字节长度
      const totalLength = this.audioQueue.reduce((acc, buffer) => acc + buffer.byteLength, 0);
      const combinedBuffer = new Uint8Array(totalLength);
      let offset = 0;

      // 将所有 audioQueue 中的 buffer 拼接到一个新的 Uint8Array 中
      for (const buffer of this.audioQueue) {
        combinedBuffer.set(new Uint8Array(buffer), offset);
        offset += buffer.byteLength;
      }

      // 清空 audioQueue，因为我们已经拼接完所有数据
      this.audioQueue = [];

      // 播放拼接的音频数据
      this.playAudio(combinedBuffer.buffer);
    }
  }

  /**
   * 停止播放并清空队列
   */
  stop(): void {
    if (this.currentSource) {
      this.currentSource.stop();
      this.currentSource = null;
      this.isPlaying = false;
    }
    this.audioQueue = [];
    // if (import.meta.env.DEV) {
    //   // eslint-disable-next-line no-console
    //   console.log('Playback stopped and queue cleared.');
    // }
  }

  /**
   * 清理资源
   */
  dispose(): void {
    this.stop();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}
